import React from 'react';

/**
 * Custom events hooks.
 *
 * See utils/dispatchCustomEvent.ts for dispatching events.
 *
 * This event listener hook abstracts away the following boilerplate:
 * ```
 * const initial = { loading: true }
 * const [itinerary, setItinerary] = useState(initial);
 * useEffect(() => {
 *   const handler = (e) => setState(e.details)
 *   document.addEventListener('trip-fetched', handler)
 *   return () => document.removeEventListener('trip-fetched', handler)
 * })
 *
 * <Itinerary loading={itinerary.loading} tripId={itinerary.id} />
 * ```
 *
 * Example:
 * ```
 * const initial = { loading: true }
 * const itinerary = useCustomEventsListener('trip-fetched', initial)
 *
 * <Itinerary loading={itinerary.loading} tripId={itinerary.id} />
 * ```
 */
export const useCustomEventListener = (eventName, initialDetailPayload) => {
  const [detailPayload, setDetailPayload] = React.useState(initialDetailPayload);
  React.useEffect(() => {
    if (typeof document === 'undefined') return () => {};
    const updatePayload = e => setDetailPayload(e.detail);
    document.addEventListener(eventName, updatePayload);
    return () => {
      document.removeEventListener(eventName, updatePayload);
    };
  }, [eventName]);
  return detailPayload;
};